import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listVideos } from '@/api/videoApi';
import type { VideoListItem } from '@/api/videoApi';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import ErrorDisplay from '@/components/shared/ErrorDisplay';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

/**
 * Pagina che elenca tutte le partite caricate in passato.
 * Ogni elemento mostra il nome del file, lo stato di elaborazione,
 * la data e un link per visualizzare i risultati.
 */
const HistoryPage: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['videos', 'list'],
    queryFn: () => listVideos(0, 50),
  });

  const getStatusBadge = (status: string): { text: string; color: string; bg: string } => {
    switch (status) {
      case 'completed': return { text: 'Completato', color: '#15803d', bg: '#dcfce7' };
      case 'failed': return { text: 'Fallito', color: '#b91c1c', bg: '#fee2e2' };
      case 'queued': return { text: 'In coda', color: '#854d0e', bg: '#fef9c3' };
      default: return { text: 'In elaborazione', color: '#1d4ed8', bg: '#dbeafe' };
    }
  };

  const handleRowClick = (video: VideoListItem) => {
    navigate(`/match/${video.id}`);
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '2rem auto', padding: '0 1rem' }}>
      <h1 style={{ color: '#1e293b', marginBottom: '1.5rem' }}>Partite Analizzate</h1>

      {isLoading && <LoadingSpinner />}

      {error && (
        <ErrorDisplay
          message="Errore nel caricamento della lista partite."
          onRetry={() => refetch()}
        />
      )}

      {data && data.videos.length === 0 && !isLoading && !error && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          <p style={{ fontSize: '1.1rem' }}>Nessuna partita caricata.</p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Carica un video
          </button>
        </div>
      )}

      {data && data.videos.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9', textAlign: 'left' }}>
                <th style={thStyle}>File</th>
                <th style={thStyle}>Stato</th>
                <th style={thStyle}>Durata</th>
                <th style={thStyle}>Caricato</th>
              </tr>
            </thead>
            <tbody>
              {data.videos.map((video) => {
                const badge = getStatusBadge(video.status);
                const formattedDuration = video.duration_seconds
                  ? `${Math.floor(video.duration_seconds / 60)}:${Math.floor(video.duration_seconds % 60).toString().padStart(2, '0')}`
                  : '—';
                const relativeTime = formatDistanceToNow(new Date(video.created_at), {
                  addSuffix: true,
                  locale: it,
                });

                return (
                  <tr
                    key={video.id}
                    onClick={() => handleRowClick(video)}
                    style={{
                      borderBottom: '1px solid #e2e8f0',
                      cursor: video.status === 'completed' ? 'pointer' : 'default',
                      backgroundColor: video.status === 'completed' ? 'transparent' : '#f8fafc',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (video.status === 'completed') e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = video.status === 'completed' ? 'transparent' : '#f8fafc';
                    }}
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{video.original_filename}</div>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: badge.color,
                          backgroundColor: badge.bg,
                        }}
                      >
                        {badge.text}
                      </span>
                    </td>
                    <td style={tdStyle}>{formattedDuration}</td>
                    <td style={tdStyle}>{relativeTime}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  fontWeight: 600,
  color: '#475569',
  borderBottom: '2px solid #cbd5e1',
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  color: '#334155',
};

export default HistoryPage;